// Shared analytics types
export type Environment =
  | 'prod'
  | 'dev'
  | 'dev2'
  | 'sit'
  | 'next'
  | 'prelive'
  | 'test'
  | 'test2'
  | any;

export interface PageProperties {
  environment: Environment;
  accountId?: any;
  isLoggedIn: boolean;
  userId?: string;
  userCity?: string;
  userCountry?: string;
  userCheckoutId: any;
  userPhoneHashed?: any;
  userEmailHashed?: any;
  isNewSite: boolean;
  pageType: string;
  userResumedCheckout: any;
  path?: string;
  url?: string;
  title?: string;
  search?: any;
  referrer?: any;
}

export interface SignedInProperties {
  userEmailHashed?: any;
  userId?: string;
}

export interface Product {
  name: string;
  price?: number;
  original_price?: number;
  product_discount?: number;
  product_coupon?: string;
  quantity: number;
  id: string;
  index?: number;
  variant: string;
  brand: string;
  category: string;
}

export interface EcommerceBaseProperties {
  cart_type?: 'acquisition' | 'upgrade';
  package_type?: 'Broadband' | 'DTH' | 'DTH + Broadband Bundle';
  coupon?: string;
  discount?: number;
  value?: number;
  tax?: number;
  order_id?: string;
  subscriber_account_number?: string;
  customer_type?: string;
  step?: number;
  step_name?: string;
  payment_method?: 'Debit' | 'Credit';
  user_id?: string;
  isNewSite?: boolean;
}

export interface ContentProperties {
  link_text?: string | null;
  link_url?: string | null;
  outbound?: any;
  name?: string | null;
  component?: string;
  context?: string | null;
  index?: number;
  user_id?: string;
  message?: string;
}

export interface FormProperties {
  form_name: string;
  form_step?: string | null;
  form_section?: string;
  field_name?: string;
  field_type?: 'Radio' | 'Checkbox' | 'Select' | 'Text' | 'Textarea' | 'Date' | 'Time';
  message?: any;
  user_id?: string;
  availability_status?: string;
}

export interface AnalyticsContextValue {
  trackPage: (properties: Partial<PageProperties>) => void;
  trackSignedIn: (properties: Partial<SignedInProperties>) => void;
  trackProductListViewed: (
    products: Product[],
    listName: string,
    extraProps?: EcommerceBaseProperties
  ) => void;
  trackProductClicked: (
    product: Product,
    listName: string,
    linkText: string,
    linkUrl: string,
    extraProps?: EcommerceBaseProperties
  ) => void;
  trackProductViewed: (product: Product, extraProps?: EcommerceBaseProperties) => void;
  trackProductAdded: (
    product: Product,
    context: string,
    extraProps?: EcommerceBaseProperties
  ) => void;
  trackProductRemoved: (product: Product, extraProps?: EcommerceBaseProperties) => void;
  trackCartViewed: (products: Product[], extraProps?: EcommerceBaseProperties) => void;
  trackCheckoutStarted: (products: Product[], extraProps?: EcommerceBaseProperties) => void;
  trackCheckoutStepCompleted: (
    step: number,
    stepName: string,
    products: Product[],
    extraProps?: EcommerceBaseProperties
  ) => void;
  trackPaymentInfoEntered: (
    paymentMethod: string,
    products: Product[],
    extraProps?: EcommerceBaseProperties
  ) => void;
  trackOrderCompleted: (
    orderId: string,
    products: Product[],
    extraProps?: EcommerceBaseProperties
  ) => void;
  trackCtaClicked: (properties: ContentProperties, onTracked?: () => void) => void;
  trackCardClicked: (properties: ContentProperties) => void;
  trackPopupViewed: (properties: ContentProperties) => void;
  trackPopupClosed: (properties: ContentProperties) => void;
  trackFlipsideViewed: (properties: ContentProperties) => void;
  trackContentExpanded: (properties: ContentProperties) => void;
  trackCarouselNavigated: (properties: ContentProperties) => void;
  trackErrorViewed: (properties: FormProperties) => void;
  trackFormStarted: (formName: string) => void;
  trackFormStepViewed: (formName: string, formStep: string) => void;
  trackFormFieldUpdated: (properties: FormProperties) => void;
  trackFormSubmitted: (formName: string) => void;
  trackFormCompleted: (formName: string) => void;
  trackFormAbandoned: (formName: string) => void;
  trackBroadbandAvailabilityChecked: (availabilityStatus: string) => void;
  trackVoucherCoupon: (
    products: Product[],
    trackEventName: string,
    extraProps?: EcommerceBaseProperties
  ) => void;
}

export interface AnalyticsProviderProps {
  children: React.ReactNode;
  defaultPageProperties: Partial<PageProperties>;
  debug?: boolean;
}

/**
 * Utility to generate link_url with full website origin and optional path
 * Use this when you need an absolute URL for external tracking
 * For anchor IDs (e.g., '#section') or relative paths, pass them directly without this utility
 * Example: getLinkUrl('/sport') => 'https://example.com/sport'
 */
export function getLinkUrl(path: string = ''): string {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin + path;
  }
  return path;
}
